import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Banner, FlexRow, NavItemBadge } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { BADGE_ITEMS } from "app/components/sidebar3/types";
import { translate } from "app/filters/i18nFilter";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { MARKETING_CHANNELS_BETA_UI_PREF_KEY } from "../../mmx-beta/consts";

const SALES_STATES = {
    legacy: "accountreview_website_marketingchannels",
    beta: "accountreview_website_marketingchannels_beta",
} as const;

const DS_STATES = {
    legacy: "digitalsuite_website_marketingchannels",
    beta: "digitalsuite_website_marketingchannels_beta",
} as const;

const StyledBanner = styled(Banner)`
    padding: 24px;
    border-radius: 6px;
    background-color: ${colorsPalettes.blue[100]};
`;

const TitleContainer = styled(FlexRow)`
    align-items: center;
`;

interface IMMXBetaBannerProps {
    isBetaMode: boolean;
}
const MmxBetaBanner: FC<IMMXBetaBannerProps> = ({ isBetaMode }) => {
    const { params } = useCurrentStateAndParams();
    const isSales = proNavigator.isSalesSolution();

    const handleToggleBetaUI = async () => {
        const states = isSales ? SALES_STATES : DS_STATES;
        const nextState = isBetaMode ? states.legacy : states.beta;

        await PreferencesService.add({
            [MARKETING_CHANNELS_BETA_UI_PREF_KEY]: !isBetaMode,
        });

        allTrackers.dispatchTrackEvent("toggle", "click", `mmx_beta_${isBetaMode ? "off" : "on"}`);

        proNavigator.go(nextState, { ...params });
    };

    const getTitleKey = () => {
        if (isBetaMode) {
            return isSales
                ? "si.trafficSources.banner.beta.title"
                : "mmx.trafficSources.banner.beta.title";
        }
        return isSales ? "si.trafficSources.banner.title" : "mmx.trafficSources.banner.title";
    };

    const getSubtitleKey = () => {
        if (isBetaMode) {
            return isSales
                ? "si.trafficSources.banner.beta.subtitle"
                : "mmx.trafficSources.banner.beta.subtitle";
        }
        return isSales ? "si.trafficSources.banner.subtitle" : "mmx.trafficSources.banner.subtitle";
    };

    return (
        <StyledBanner
            onButtonClick={handleToggleBetaUI}
            buttonText={
                isBetaMode
                    ? translate("mmx.trafficSources.banner.go.back")
                    : translate("mmx.trafficSources.banner.try.it.now")
            }
            buttonType={isBetaMode ? "flat" : "primary"}
            title={
                <FlexColumn>
                    <TitleContainer>
                        <Text
                            fontVariant="subtitle1"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate(getTitleKey())}
                        </Text>
                        <NavItemBadge type={isBetaMode ? BADGE_ITEMS.BETA : BADGE_ITEMS.NEW} />
                    </TitleContainer>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate(getSubtitleKey())}
                    </Text>
                    {!isBetaMode && isSales ? (
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("si.trafficSources.banner.note")}
                        </Text>
                    ) : null}
                </FlexColumn>
            }
            CustomIcon={
                <SWReactIcons
                    iconName="affiliates-green"
                    size="lg"
                />
            }
        />
    );
};

export default MmxBetaBanner;
